/* eslint-disable react/require-optimization */
import React, {
    type ComponentProps,
    type ComponentRef,
    type ComponentType, createElement, forwardRef,
    type ForwardRefExoticComponent,
    type PropsWithoutRef,
    type RefAttributes,
} from 'react';

interface FallbackProps {
    error: Error;
}

interface CommonErrorBoundaryProps {
    onError?: (error: Error, info: React.ErrorInfo) => void;
}

interface ErrorBoundaryPropsWithComponent extends CommonErrorBoundaryProps {
    fallback?: never;
    FallbackComponent: React.ComponentType<FallbackProps>;
    fallbackRender?: never;
}

declare function FallbackRender(
    props: FallbackProps,
): React.ReactElement<unknown, string | React.FunctionComponent | typeof React.Component> | null;

interface ErrorBoundaryPropsWithRender extends CommonErrorBoundaryProps {
    fallback?: never;
    FallbackComponent?: never;
    fallbackRender: typeof FallbackRender;
}

interface ErrorBoundaryPropsWithFallback extends CommonErrorBoundaryProps{
    fallback: React.ReactElement<unknown, string | React.FunctionComponent | typeof React.Component> | null;
    FallbackComponent?: never;
    fallbackRender?: never;
}

type ErrorBoundaryProps =
    | ErrorBoundaryPropsWithFallback
    | ErrorBoundaryPropsWithComponent
    | ErrorBoundaryPropsWithRender;

type ErrorBoundaryState = {error: Error | null};

const initialState: ErrorBoundaryState = {error: null};

type BoundaryComponentProps = React.PropsWithRef<React.PropsWithChildren<ErrorBoundaryProps>>;

class ErrorBoundary extends React.Component<BoundaryComponentProps, ErrorBoundaryState> {
    static getDerivedStateFromError(error: Error) {
        return {error};
    }

    state = initialState;

    componentDidCatch(error: Error, info: React.ErrorInfo) {
        this.props.onError?.(error, info);
    }

    render() {
        const {error} = this.state;

        const {fallbackRender, FallbackComponent, fallback} = this.props;

        if (error !== null) {
            const props = {error};

            if (React.isValidElement(fallback)) {
                return fallback;
            } else if (typeof fallbackRender === 'function') {
                return fallbackRender(props);
            } else if (FallbackComponent) {
                return <FallbackComponent {...props} />;
            }
            throw new Error(
                'error-boundary requires either a fallback, fallbackRender, or FallbackComponent prop',
            );
        }

        return this.props.children;
    }
}

function withErrorBoundary<T extends ComponentType<any>>(
    component: T,
    errorBoundaryProps: ErrorBoundaryProps,
): ForwardRefExoticComponent<
    PropsWithoutRef<ComponentProps<T>> & RefAttributes<ComponentRef<T>>
    > {
    const Wrapped = forwardRef<ComponentRef<T>, ComponentProps<T>>((props, ref) =>
        createElement(
            ErrorBoundary,
            errorBoundaryProps,
            createElement(component, {...props, ref}),
        ),
    );

    // Format for display in DevTools
    const name = component.displayName || component.name || 'Unknown';
    Wrapped.displayName = `withErrorBoundary(${name})`;

    return Wrapped;
}

export {ErrorBoundary, withErrorBoundary};
